import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import { useAuthState, useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { useEffect, useState } from 'react'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { Button } from '@app/components/core/button/Button'
import { DashboardListCard } from '@app/components/blocks/card/DashboardListCard'
import { INITIATIVE_USER_ROLES } from '@schema/initiative'
import listInitiativeFunction from 'functions/app/dashboard/profile/initiative/list/based.config'
import { DahsboardListInitiativeModel } from 'functions/app/dashboard/profile/initiative/list'
import { labelAddressBuilder } from '@app/util/helpers'
import searchForGroupImage from '@app/assets/images/people_dreaming.png'
import { FollowingEmptyState } from './FollowingEmptyState'

export const FollowedInitiatives = () => {
  const translations = useTranslations().app.dashboard.followed
  const commonTranslations = useTranslations().app.common
  const client = useClient()
  const authState = useAuthState()
  const [followedInitiatives, setFollowedInitiatives] = useState<
    DahsboardListInitiativeModel[]
  >([])
  const [_, navigate] = useLocation()

  useEffect(() => {
    const close = client
      .query(listInitiativeFunction.name, {
        userId: authState.userId,
        role: [INITIATIVE_USER_ROLES.follower, INITIATIVE_USER_ROLES.member],
        includeAllInitiativeUsers: false,
      })
      .subscribe((result) => {
        setFollowedInitiatives(result)
      })
    return () => close()
  }, [client, authState.userId])

  const unfollow = async (initiativeId: number) => {
    await client.call('unfollow-initiative', {
      initiativeId: initiativeId,
    })
  }

  const leaveMember = async (initiativeId: number) => {
    await client.call('leave-initiative', {
      initiativeId: initiativeId,
    })
  }

  if (followedInitiatives.length === 0) {
    return (
      <FollowingEmptyState
        title={translations.initiatives}
        message={translations.youFollowNoInitiatives}
        imageSrc={searchForGroupImage}
        imageAlt={translations.discoverInitiatives}
        discoverHref={pages.initiatives}
        discoverLabel={translations.discoverInitiatives}
      />
    )
  }

  return (
    <Column alignItems="stretch">
      <Title.H4 style={{ marginBottom: '0' }}>
        {translations.initiatives}
      </Title.H4>
      {followedInitiatives.map((initiative) => {
        const role = initiative.users[0].$role
        const isFollower = role === INITIATIVE_USER_ROLES.follower
        return (
          <DashboardListCard
            key={initiative.slug}
            title={initiative.title}
            tag={commonTranslations.initiativeUserRole[role]}
            image={initiative.images?.[0] ?? defaultProjectPicture}
            location={labelAddressBuilder(
              initiative.location.address,
              commonTranslations.notSpecified,
            )}
            status={initiative.phase.name}
            onClick={() => navigate(pages.initiative(initiative.slug))}
            action={
              <Button
                variant="secondary"
                size="small"
                label={translations.unfollow}
                onClick={(e) => {
                  e.stopPropagation()
                  if (isFollower) {
                    void unfollow(initiative.id)
                  } else {
                    void leaveMember(initiative.id)
                  }
                }}
              />
            }
          />
        )
      })}
    </Column>
  )
}
